import { useTranslation } from 'react-i18next';
import { DpoContactInfo } from '../DpoContactInfo';

/**
 * 🔴 Блокът вече НЕ събира общо съгласие — само чл. 8, и само когато е нужно.
 *
 * Махнати са три чекбокса (Р-6.2/Р-6.10): `dataProcessing` беше УСЛОВИЕ да се
 * подпише каквото и да е друго, тоест събираше „съгласие", което не е свободно
 * дадено; `photoUse` и `emailCommunication` дублираха `PHOTO_CONSENT` (там ДА/НЕ
 * е изричен, по четири канала) и се презаписваха при всяко подписване.
 *
 * ⚠️ Защо излезе и самият `dataProcessing`, а не само блокадата: чекбокс
 * „Съгласен съм с обработка на лични данни" под декларация, чието основание е
 * ДОГОВОР или ЗАКОН, внушава на родителя, че основанието е съгласие и може да
 * се оттегли. Оставен без блокада, той сваля принудата и запазва заблудата.
 */
interface GdprConsentState {
  parentalConsent: boolean;
}

interface GdprConsentProps {
  consent: GdprConsentState;
  onChange: (consent: GdprConsentState) => void;
  /** True if any targeted child is < 16 (GDPR Art. 8). Shows parental consent block. */
  requiresParentalConsent?: boolean;
  /** True if any targeted child has unknown birthDate — treated as minor. */
  hasUnknownAge?: boolean;
}

export function GdprConsent({ consent, onChange, requiresParentalConsent = false, hasUnknownAge = false }: GdprConsentProps) {
  const { t } = useTranslation();

  const handleChange = (field: keyof GdprConsentState, value: boolean) => {
    onChange({ ...consent, [field]: value });
  };

  // 🔴 Няма ли чл. 8 съгласие за искане, блокът вече не е формуляр — остава само
  // информацията по чл. 13 (кой е DPO). Рамка и заглавие около нея обещават
  // действие, което не се иска.
  //
  // ⚠️ И по-конкретното: `DpoContactInfo` се скрива САМ, когато няма нито име,
  // нито имейл — а началното му състояние е празно, докато Firestore отговори.
  // Тоест рамка + заглавие тук даваха ПРАЗНА кутия при всяко отваряне на модала,
  // и завинаги за училище без вписан DPO. Преди тази промяна случаят беше
  // невъзможен, защото трите махнати чекбокса винаги пълнеха блока.
  if (!requiresParentalConsent) {
    return <DpoContactInfo compact />;
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3">
      <h3 className="font-medium text-gray-900">{t('signModal.gdprConsent.title')}</h3>

      {/* Дотук имаше `{requiresParentalConsent && …}`. След горния ранен изход
          условието е винаги истина — оставено, то подсказва на следващия читател
          клон, който не съществува. */}
      <div className="pt-3 mt-3 border-t border-amber-200 bg-amber-50 -mx-4 -mb-3 px-4 pb-3 rounded-b-lg">
        <p className="text-xs font-medium text-amber-900 mb-2">
          ⚖️ {t('signModal.gdprConsent.parentalConsentTitle')}
        </p>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={consent.parentalConsent}
            onChange={(e) => handleChange('parentalConsent', e.target.checked)}
            className="mt-1"
          />
          <span className="text-sm text-amber-900">
            {t('signModal.gdprConsent.parentalConsentBody')}
          </span>
        </label>
        {hasUnknownAge && (
          <p className="text-xs text-amber-800 mt-2">
            {t('signModal.gdprConsent.unknownAgeWarning')}
          </p>
        )}
      </div>

      <DpoContactInfo compact className="pt-3 border-t border-gray-200" />
    </div>
  );
}
